import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ImageCropModal from './ImageCropModal';
import ViewProfilePictureModal from './ViewProfilePictureModal';
import userService from '../../services/userService';
import './ProfilePicture.scss';

const ProfilePicture = ({ size = 'large', editable = true }) => {
  const { user, updateUser } = useAuth();
  const [showCropModal, setShowCropModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const getProfileImageUrl = () => {
    if (user?.profile_picture) {
      return `/api/profile-picture/${user.profile_picture}`;
    }
    return '/unset-profile-pic.png';
  };

  const handleImageUpload = async (croppedImageBlob) => {
    setIsUploading(true);
    try {
      // userService now returns { ok: boolean, data: object }
      const result = await userService.uploadProfilePicture(croppedImageBlob);

      if (result.ok) {
        // Update user context with new profile picture
        updateUser({
          ...user,
          profile_picture: result.data.filename
        });
        setShowCropModal(false);
        setShowViewModal(false);
      } else {
        const errorMsg = result.data?.error || 'Upload failed';
        console.error('Upload failed:', errorMsg);
        alert(`Upload failed: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload profile picture. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!user?.profile_picture) return;

    if (!window.confirm('Are you sure you want to remove your profile picture?')) {
      return;
    }

    setIsUploading(true);
    try {
      // userService now returns { ok: boolean, data: object }
      const result = await userService.deleteProfilePicture();

      if (result.ok) {
        // Update user context to remove profile picture
        updateUser({
          ...user,
          profile_picture: null
        });
        setShowViewModal(false);
      } else {
        const errorMsg = result.data?.error || 'Delete failed';
        console.error('Delete failed:', errorMsg);
        alert(`Failed to remove profile picture: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to remove profile picture. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={`profile-picture profile-picture--${size}`}>
      <div 
        className={`profile-picture__container ${editable ? 'profile-picture__container--editable' : ''}`}
        onClick={editable ? () => setShowViewModal(true) : undefined}
      >
        <img
          src={getProfileImageUrl()}
          alt={user?.username || 'Profile'}
          className="profile-picture__image"
          onError={(e) => {
            e.target.src = '/unset-profile-pic.png';
          }}
        />
        
        {editable && (
          <div className="profile-picture__overlay">
            <div className="profile-picture__overlay-icon">📷</div>
            <div className="profile-picture__overlay-text">
              {isUploading ? 'Uploading...' : 'View'}
            </div>
          </div>
        )}
        
        {isUploading && (
          <div className="profile-picture__loading">
            <div className="profile-picture__spinner"></div>
          </div>
        )}
      </div>

      {showViewModal && (
        <ViewProfilePictureModal
          onClose={() => setShowViewModal(false)}
          onReplace={() => {
            setShowViewModal(false);
            setShowCropModal(true);
          }}
          onDelete={handleRemoveProfilePicture}
          isDeleting={isUploading}
        />
      )}

      {showCropModal && (
        <ImageCropModal
          onSave={handleImageUpload}
          onCancel={() => setShowCropModal(false)}
          isUploading={isUploading}
        />
      )}
    </div>
  );
};

export default ProfilePicture;